import type { BookProgress, ProgressFile } from '../types';
import { EMPTY_PROGRESS } from '../types';

/**
  * Merges two progress files **per book**, keeping whichever entry was written
  * most recently.
  *
  * Merging per book rather than taking the newer file wholesale is the detail
  * that makes multi-device use safe: if you listen to book A on your phone and
  * book B on your laptop on the same afternoon, a whole-file last-write-wins
  * would silently discard one of them.
  */
export function mergeProgress(a: ProgressFile | null, b: ProgressFile | null): ProgressFile {
    if (!a) return b ?? EMPTY_PROGRESS;
    if (!b) return a;

    const books: Record<string, BookProgress> = { ...a.books };
    for (const [id, entry] of Object.entries(b.books)) {
      const mine = books[id];
      if (!mine || isNewer(entry, mine)) books[id] = entry;
    }
    return { version: 1, books };
}

export function isNewer(candidate: BookProgress, existing: BookProgress): boolean {
    return Date.parse(candidate.updatedAt) > Date.parse(existing.updatedAt);
}

/** Ignore drift below this — no point prompting over a couple of seconds. */
export const RESUME_PROMPT_THRESHOLD_SEC = 30;

/**
  * Decides whether an incoming remote entry deserves a "resume from your other
  * device?" prompt, rather than being applied silently or discarded.
  *
  * Returns true only when the remote entry is genuinely newer *and* far enough
  * away to be worth interrupting for. Anything else would either yank playback
  * mid-sentence or nag over nothing.
  */
export function shouldPromptResume(
    remote: BookProgress | undefined,
    local: BookProgress | undefined,
): boolean {
    if (!remote) return false;
    if (!local) return false;
    if (!isNewer(remote, local)) return false;
    return Math.abs(absoluteish(remote) - absoluteish(local)) > RESUME_PROMPT_THRESHOLD_SEC;
}

/** Rough ordering value for comparing two positions that may be in different
  * tracks. Exact totals are not needed — only "are these far apart". */
function absoluteish(p: BookProgress): number {
   return p.trackIndex * 1e6 + p.positionSec;
}

export function makeEntry(
   trackIndex: number,
   positionSec: number,
   durationSec: number,
   device: string,
   finished = false,
): BookProgress {
   return {
      trackIndex,
      positionSec: Math.max(0, Math.round(positionSec * 10) / 10),
      durationSec,
      updatedAt: new Date().toISOString(),
      device,
      finished,
   };
}

/** A book counts as finished once you are within 30s of the end of the last track. */
export function isEffectivelyFinished(
   trackIndex: number,
   trackCount: number,
   positionSec: number,
   trackDurationSec: number,
): boolean {
   if (trackIndex !== trackCount - 1) return false;
   if (!Number.isFinite(trackDurationSec) || trackDurationSec <= 0) return false;
   return trackDurationSec - positionSec <= 30;
}
